import React, { useState, useEffect, useRef } from "react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { useLocation } from "react-router-dom";
import {
  User,
  MapPin,
  Globe,
  CreditCard,
  Calendar,
  FileSignature,
  PenLine,
  Loader2,
  Download,
} from "lucide-react";
import { authFetch } from '../../HttpClient';
import { uploadFileToBlob } from '../../utils/uploadFileToBlob';

import { dialog } from "../../components/ui/Dialog";

// Modèles de mandat par cabinet créancier (PDF affiché + Word généré)
const MANDAT_TEMPLATES = {
  "SAINT-PIERRE": {
    pdf: "https://nrcdumqfyl1z2bwl.public.blob.vercel-storage.com/visuel%20mandat%20pdf/Mandat%20-%20SAINT-PIERRE%20-%20visuel.pdf",
    docx: "/Mandat - SAINT-PIERRE.docx",
  },
  // LE TAMPON (défaut historique)
  default: {
    pdf: "https://nrcdumqfyl1z2bwl.public.blob.vercel-storage.com/visuel%20mandat%20pdf/Mandat%20-%20visuel.pdf",
    docx: "/Mandat.docx",
  },
};

function formatFileSize(bytes) {
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function Mandat() {
  const today = new Date().toISOString().split("T")[0];
  const [scanning, setScanning] = useState(false);
  const [ribFileError, setRibFileError] = useState("");
  const ribFileInputRef = useRef(null);
  const [isPdf, setIsPdf] = useState(false);
  const [ribMessage, setRibMessage] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0"); // HH
  const minutes = String(now.getMinutes()).padStart(2, "0"); // mm
  const currentTime = `${hours}:${minutes}`; // HH:mm
  const [signingLink, setSigningLink] = useState(null);
  const location = useLocation();
  const [client, setClient] = useState(null);
  const [showValidationActions, setShowValidationActions] = useState(false);
  const [isNormalSignature] = useState(true);

  const handleValidateClick = async () => {
    const confirmAction = await dialog.confirm(
      "Cette action va créer définitivement le mandat. Voulez-vous continuer ?"
    );

    if (confirmAction) {
      setShowValidationActions(true);
    }
  };

  useEffect(() => {
    const fetchClient = async () => {
      const params = new URLSearchParams(location.search);
      const clientId = params.get("clientId");

      if (!clientId) return;

      try {
        const response = await authFetch(`https://backend-myalfa.vercel.app/api/clients/${clientId}`
        );

        const data = await response.json();
        setClient(data);
      } catch (error) {
        console.error("Erreur récupération client :", error);
      }
    };

    fetchClient();
  }, [location]);

  const generateDate = () => {
    const today = new Date();

    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();

    return `${day}${month}${year}`;
  };

  const [formData, setFormData] = useState({
    rum: "",
    nom: client?.nom || "",
    email: client?.email || "",
    rue: "",
    adresse_complement: "",
    code_postal: "",
    pays: "",
    bic: "",
    rib: "",
    ribDocument: null,
    dateSignature: today,
    heureSignature: currentTime,
    lieu: "",
    // Cabinet juridique créancier, choisi par le collaborateur en haut du
    // formulaire — pilote le template (PDF/Word) et la séquence RUM
    cabinet: "LE TAMPON",
  });

  // Templates (PDF affiché + Word généré) du cabinet sélectionné
  const mandatTemplate =
    MANDAT_TEMPLATES[formData.cabinet] || MANDAT_TEMPLATES.default;

  // Le RUM est régénéré à chaque changement de cabinet : chaque cabinet a
  // sa propre séquence autonome
  useEffect(() => {
    const generateRum = async () => {
      try {
        const response = await authFetch(
          `https://backend-myalfa.vercel.app/api/mandats/last?cabinet=${encodeURIComponent(
            formData.cabinet
          )}`
        );
        const data = await response.json();

        const nextNumber = String(data.nextNumber).padStart(6, "0");

        const rumGenerated = `REF${generateDate()}${nextNumber}`;

        setFormData((prev) => ({
          ...prev,
          rum: rumGenerated,
        }));
      } catch (error) {
        console.error("Erreur génération RUM :", error);
      }
    };

    generateRum();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.cabinet]);

  useEffect(() => {
    if (!client) return;

    setFormData((prev) => ({
      ...prev,
      nom: client?.nom || "",
      email: client.email || "",
      rue: client.adresse || "",
    }));
  }, [client]);

  console.log("pdf", isPdf);

  const [signing, setSigning] = useState(false); // loading submit
  const [download, setDonwload] = useState(false); // loading submit

  const scanRibAutomatically = async (file) => {
    try {
      setScanning(true);
      setRibMessage("");

      // Upload direct navigateur → Blob (contourne la limite 4,5 Mo) : le
      // serveur retélécharge le fichier depuis Blob pour le parser (pas de
      // stockage, juste une lecture — voir ribController.js).
      const documentUrl = await uploadFileToBlob(
        file,
        "https://backend-myalfa.vercel.app/api/extract-rib/upload-token"
      );

      const response = await authFetch("https://backend-myalfa.vercel.app/api/extract-rib",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ documentUrl }),
        }
      );

      const data = await response.json();

      const ribValide = data.rib && data.rib.trim() !== "";
      const bicValide = data.bic && data.bic.trim() !== "";

      if (ribValide && bicValide) {
        setFormData((prev) => ({
          ...prev,
          rib: data.rib,
          bic: data.bic,
        }));

        setRibMessage(
          "Veuillez vérifier les informations récupérées avant de signer."
        );
      } else if (!ribValide && !bicValide) {
        setRibMessage(
          "Les informations n'ont pu être récupérées.\nMerci de saisir ci-dessous l'IBAN et le code BIC."
        );
      } else if (!ribValide) {
        setRibMessage(
          "L'IBAN n'a pas pu être récupéré.\nMerci de le saisir ci-dessous."
        );
      } else if (!bicValide) {
        setRibMessage(
          "Le code BIC n'a pas pu être récupéré.\nMerci de le saisir ci-dessous."
        );
      }
    } catch (error) {
      console.error("Erreur scan RIB:", error);

      setRibMessage(
        "Les informations n'ont pu être récupérées.\nMerci de saisir ci-dessous l'IBAN et le code BIC."
      );
    } finally {
      setScanning(false);
    }
  };

  // Retire le fichier RIB actuel (trop volumineux, même après compression) —
  // le formulaire reste bloqué tant que ce n'est pas fait, voir isFormBlocked.
  const handleRemoveRibDocument = () => {
    setFormData((prev) => ({ ...prev, ribDocument: null }));
    setRibFileError("");
    setRibMessage("");
    if (ribFileInputRef.current) ribFileInputRef.current.value = "";
  };

  // Upload direct navigateur → Blob (contourne la limite 4,5 Mo des Vercel
  // Functions, 25/09/2026) : plus besoin de compresser/bloquer le fichier
  // côté client pour rester sous cette limite, elle ne s'applique plus à ce
  // flux. Le scan auto reste réservé aux PDF (pdf-parse ne lit pas les
  // images).
  const handleChange = async (e) => {
    const { name, value, files } = e.target;

    if (name === "ribDocument" && files && files[0]) {
      const file = files[0];
      setRibFileError("");
      setIsPdf(file.type === "application/pdf");
      setFormData((prev) => ({ ...prev, ribDocument: file }));

      if (file.type === "application/pdf") {
        scanRibAutomatically(file);
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatDateFR = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const downloadMandat = async () => {
    try {
      setDonwload(true);

      // 1️⃣ Charger le template Word
      const response = await fetch(encodeURI(mandatTemplate.docx));

      if (!response.ok) {
        throw new Error("Erreur lors du chargement du fichier Word");
      }

      const arrayBuffer = await response.arrayBuffer();

      // 2️⃣ Charger le zip
      const zip = new PizZip(arrayBuffer);

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });

      // 3️⃣ Remplir les variables
      doc.render({
        ...formData,
        dateSignature: formatDateFR(formData.dateSignature),
      });

      // 4️⃣ Générer le Word rempli
      const output = doc.getZip().generate({ type: "blob" });

      // 5️⃣ Préparer l'envoi API
      const formDataToSend = new FormData();
      formDataToSend.append("file", output, mandatTemplate.docx.slice(1));

      // 6️⃣ Envoi API conversion PDF
      const uploadResponse = await authFetch("https://backend-myalfa.vercel.app/api/upload",
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error("Erreur lors de l'envoi du fichier");
      }

      // 7️⃣ Récupérer l'URL du PDF
      const { pdfUrl } = await uploadResponse.json();
      setPdfUrl(pdfUrl);

      // 8️⃣ Télécharger le PDF (FORCÉ)
      const pdfResponse = await fetch(pdfUrl);
      const blob = await pdfResponse.blob();

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "Mandat.pdf";

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Erreur téléchargement mandat:", error);
      dialog.alert("Une erreur est survenue : " + error.message);
    } finally {
      setDonwload(false);
    }
  };

  const createAndDownloadMandat = async () => {
    try {
      setDonwload(true);

      // 1️⃣ Charger le template Word
      const response = await fetch(encodeURI(mandatTemplate.docx));
      if (!response.ok)
        throw new Error("Erreur lors du chargement du fichier Word");
      const arrayBuffer = await response.arrayBuffer();

      // 2️⃣ Charger le zip et remplir le doc
      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });
      doc.render({
        ...formData,
        dateSignature: formatDateFR(formData.dateSignature),
      });

      // 3️⃣ Générer le Word
      const output = doc.getZip().generate({ type: "blob" });

      // 4️⃣ Upload → PDF
      const formDataToSend = new FormData();
      formDataToSend.append("file", output, mandatTemplate.docx.slice(1));

      const uploadResponse = await authFetch("https://backend-myalfa.vercel.app/api/upload",
        {
          method: "POST",
          body: formDataToSend,
        }
      );
      if (!uploadResponse.ok)
        throw new Error("Erreur lors de la conversion PDF");

      const { pdfUrl } = await uploadResponse.json();
      setPdfUrl(pdfUrl);

      // 5️⃣ Télécharger le PDF
      const pdfResponse = await fetch(pdfUrl);
      const blob = await pdfResponse.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "Mandat.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 6️⃣ Préparer FormData pour création du mandat en BDD
      const payloadFormData = new FormData();
      payloadFormData.append("rum", formData.rum);
      payloadFormData.append("nom", formData.nom);
      payloadFormData.append("email", formData.email);
      payloadFormData.append("rue", formData.rue);
      payloadFormData.append("adresse_complement", formData.adresse_complement);
      payloadFormData.append("code_postal", formData.code_postal);
      payloadFormData.append("pays", formData.pays);
      payloadFormData.append("bic", formData.bic);
      payloadFormData.append("rib", formData.rib);
      payloadFormData.append("dateSignature", formData.dateSignature);
      payloadFormData.append("lieu", formData.lieu);
      payloadFormData.append("clientId", client.id);
      payloadFormData.append("cabinet", formData.cabinet);
      // La timeline client (ligne "Création de mandat") est désormais
      // alimentée par le backend lui-même (voir logMandatHistorique dans
      // MandatSignController.js) — il lui faut ce lien pour le bouton "Voir
      // le mandat PDF".
      payloadFormData.append("pdfUrl", pdfUrl);

      // 7️⃣ Ajouter ribDocument si présent — upload direct navigateur → Blob
      // (contourne la limite 4,5 Mo) avant l'envoi du reste du formulaire.
      if (formData.ribDocument) {
        const documentUrl = await uploadFileToBlob(
          formData.ribDocument,
          "https://backend-myalfa.vercel.app/api/mandat/upload-token"
        );
        payloadFormData.append("documentUrl", documentUrl);
      }

      // 8️⃣ Envoyer à l'API pour créer le mandat
      const createResponse = await authFetch("https://backend-myalfa.vercel.app/api/mandat/create",
        { method: "POST", body: payloadFormData }
      );
      if (!createResponse.ok)
        throw new Error("Erreur lors de la création du mandat");

      console.log("Mandat créé en base avec succès !");

      // 🔹 Cleanup URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
      dialog.alert("Erreur : " + error.message);
    } finally {
      setDonwload(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSigning(true);

      // 1️⃣ Charger le template Word depuis /public
      const response = await fetch(encodeURI(mandatTemplate.docx));

      if (!response.ok) {
        throw new Error("Erreur lors du chargement du fichier Word");
      }

      const arrayBuffer = await response.arrayBuffer();

      // 2️⃣ Charger le zip
      const zip = new PizZip(arrayBuffer);

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });

      // 3️⃣ Remplir les variables avec formData
      doc.render({
        ...formData,
        dateSignature: formatDateFR(formData.dateSignature),
      });

      // 4️⃣ Générer le Word rempli
      const output = doc.getZip().generate({ type: "blob" });

      // 5️⃣ Préparer l'envoi à l'API
      const formDataToSend = new FormData();
      formDataToSend.append("file", output, mandatTemplate.docx.slice(1));

      // 6️⃣ Envoyer à l'API pour conversion PDF
      const uploadResponse = await authFetch("https://backend-myalfa.vercel.app/api/upload",
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error("Erreur lors de l'envoi du fichier");
      }

      // 7️⃣ Récupération de l'URL du PDF
      const { pdfUrl } = await uploadResponse.json();

      setPdfUrl(pdfUrl);

      // 6️⃣ Préparer FormData pour l'API DOCUSIGN
      const payloadFormData = new FormData();
      payloadFormData.append("pdfUrl", pdfUrl);
      payloadFormData.append(
        "emailSubject",
        `Signature du mandat - ${formData.nom}`
      );
      payloadFormData.append("clientId", client.id);
      payloadFormData.append("rum", formData.rum);
      payloadFormData.append("nom", formData.nom);
      payloadFormData.append("rue", formData.rue);
      payloadFormData.append("adresse_complement", formData.adresse_complement);
      payloadFormData.append("code_postal", formData.code_postal);
      payloadFormData.append("pays", formData.pays);
      payloadFormData.append("bic", formData.bic);
      payloadFormData.append("rib", formData.rib);
      payloadFormData.append("dateSignature", formData.dateSignature);
      payloadFormData.append("lieu", formData.lieu);
      payloadFormData.append("cabinet", formData.cabinet);

      // Ajouter ribDocument si présent — upload direct navigateur → Blob
      // (contourne la limite 4,5 Mo) avant l'envoi du reste du formulaire.
      if (formData.ribDocument) {
        const documentUrl = await uploadFileToBlob(
          formData.ribDocument,
          "https://backend-myalfa.vercel.app/api/mandat/upload-token"
        );
        payloadFormData.append("documentUrl", documentUrl);
      }

      // Ajouter signers (JSON string car FormData ne supporte pas objets directement)
      payloadFormData.append(
        "signers",
        JSON.stringify([
          {
            name: formData.nom,
            email: formData.email,
            signatures: [{ pageNumber: 1, xPosition: 150, yPosition: 625 }],
          },
        ])
      );

      const signResponse = await authFetch("https://backend-myalfa.vercel.app/api/mandat/send",
        { method: "POST", body: payloadFormData }
      );

      const signData = await signResponse.json();

      if (signData.signingLinks && signData.signingLinks.length > 0) {
        const signingLink = signData.signingLinks[0].url;
        setSigningLink(signingLink);

        // 9️⃣ Envoyer le mail au client
        const emailPayload = {
          email: formData.email,
          link: signingLink,
          subject: `Signature du mandat - ${formData.nom}`,
        };

        const emailApiUrl = isNormalSignature
          ? "https://backend-myalfa.vercel.app/api/mandat/send-email-link-normal"
          : "https://backend-myalfa.vercel.app/api/mandat/send-email-link-normal";

        const emailResponse = await authFetch(emailApiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(emailPayload),
        });

        if (!emailResponse.ok) {
          throw new Error("Erreur lors de l'envoi de l'email au client");
        }

        dialog.alert("Mail à bien été envoyé au client");
      }
      // Plus d'appel manuel à /api/historique-client ici : le backend logue
      // désormais lui-même "Création de mandat" avec le vrai lien PDF (voir
      // logMandatHistorique dans MandatSignController.js, appelé depuis
      // sendMandatDocument/addMandatDocument), pour que ça marche aussi
      // depuis les autres parcours d'envoi (formulaire public de signature).
    } catch (error) {
      console.error("Erreur génération mandat:", error);
      dialog.alert("Une erreur est survenue : " + error.message);
    } finally {
      setSigning(false);
    }
  };

  if (!client) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 p-4 lg:p-10 relative">
        <div className="max-w-7xl mx-auto bg-white shadow-2xl rounded-3xl overflow-hidden">
          <div className="flex flex-col lg:flex-row h-full animate-pulse">
            {/* FORMULAIRE */}
            <div className="w-full lg:w-1/2 p-8 lg:p-12 bg-white">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-8 rounded-lg bg-slate-200" />
                <div className="h-7 w-56 rounded bg-slate-200" />
              </div>
              <div className="space-y-6">
                {["w-16", "w-24", "w-20", "w-24", "w-12", "w-32", "w-10", "w-20"].map(
                  (w, i) => (
                    <div key={i}>
                      <div className={`h-3 ${w} rounded bg-slate-200 mb-2`} />
                      <div className="h-11 rounded-xl bg-slate-100 border border-slate-200" />
                    </div>
                  )
                )}
              </div>
              <div className="flex flex-col gap-3 mt-10">
                <div className="h-11 rounded-md bg-slate-200" />
                <div className="h-11 rounded-md bg-slate-200" />
              </div>
            </div>

            {/* PDF VIEWER */}
            <div className="relative w-full lg:w-1/2 mt-8 lg:mt-20 border lg:border-l">
              <div className="w-full lg:aspect-[210/297] h-64 lg:h-auto bg-slate-100" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Bloque la création/l'envoi tant qu'un fichier RIB est en cours de
  // compression ou reste trop volumineux (voir handleChange) — l'utilisateur
  // doit le supprimer ou le remplacer avant de pouvoir continuer.
  const isRibBlocked = Boolean(ribFileError);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 p-4 lg:p-10 relative">
      {/* Logo */}

      <div className="max-w-7xl mx-auto bg-white shadow-2xl rounded-3xl overflow-hidden">
        <div className="flex flex-col lg:flex-row h-full">
          {/* FORMULAIRE */}
          <div className="w-full lg:w-1/2 p-8 lg:p-12 bg-white">
            <div className="flex items-center gap-3 mb-5">
              <FileSignature className="text-[#8B1538]" size={32} />
              <h2 className="text-3xl font-bold text-slate-800 m-0">
                Signature du Mandat
              </h2>
              {/* <input
                type="text"
                name="rum"
                value={formData.rum}
                onChange={handleChange}
                className="ml-auto text-sm font-medium text-slate-500  border rounded-xl w-32 px-3 py-2 focus:ring-2 focus:ring-[#8B1538] transition-all duration-200 mt-2"
                placeholder="RUM"
              /> */}
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 ">
              {formData.email === "" && (
                <p className="text-xs text-red-500">
                  Alerte : Email vide, Veuillez saisir dans la fiche client le
                  mail.
                </p>
              )}
              {/* Cabinet créancier — conditionne le modèle du mandat et la séquence RUM */}
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">
                  Cabinet
                </label>
                <div className="flex items-center border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-[#8B1538]">
                  <FileSignature className="text-slate-400 mr-2" size={18} />
                  <select
                    name="cabinet"
                    value={formData.cabinet}
                    onChange={handleChange}
                    className="w-full outline-none bg-white text-slate-700"
                  >
                    <option value="LE TAMPON">LE TAMPON</option>
                    <option value="SAINT-PIERRE">SAINT-PIERRE</option>
                  </select>
                </div>
              </div>
              {/* Nom */}
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">
                  Nom complet ( {formData.email} )
                </label>
                <div className="flex items-center border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-[#8B1538]">
                  <User className="text-slate-400 mr-2" size={18} />
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="w-full outline-none "
                    placeholder="Votre nom complet"
                  />
                </div>
              </div>

              {/* Adresse */}
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">
                  Adresse
                </label>
                <div className=" mb-4 flex items-center border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-[#8B1538]">
                  <MapPin className="text-slate-400 mr-2" size={18} />
                  <input
                    type="text"
                    name="rue"
                    value={formData.rue}
                    onChange={handleChange}
                    required
                    className="w-full outline-none "
                    placeholder="Numéro de rue"
                  />
                </div>
                <div className=" mb-4 flex items-center border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-[#8B1538]">
                  <MapPin className="text-slate-400 mr-2" size={18} />
                  <input
                    type="text"
                    name="adresse_complement"
                    value={formData.adresse_complement}
                    onChange={handleChange}
                    className="w-full outline-none "
                    placeholder="Complément d'adresse (bâtiment, étage...)"
                  />
                </div>
                <div className="flex items-center border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-[#8B1538]">
                  <MapPin className="text-slate-400 mr-2" size={18} />
                  <input
                    type="text"
                    name="code_postal"
                    value={formData.code_postal}
                    onChange={handleChange}
                    required
                    className="w-full outline-none "
                    placeholder="Code postal et ville"
                  />
                </div>
              </div>

              {/* Pays */}
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">
                  Pays
                </label>
                <div className="flex items-center border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-[#8B1538]">
                  <Globe className="text-slate-400 mr-2" size={18} />
                  <input
                    type="text"
                    name="pays"
                    value={formData.pays}
                    onChange={handleChange}
                    required
                    className="w-full outline-none "
                    placeholder="Votre pays"
                  />
                </div>
              </div>

              {/* RIB */}
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">
                  RIB
                </label>
                {/* Document RIB */}
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    {/* Input file */}
                    <div className=" m-0 flex items-center border rounded-xl px-3 py-2 flex-1 focus-within:ring-2 focus-within:ring-[#8B1538] transition-all duration-200">
                      <CreditCard className="text-slate-400 mr-2" size={18} />
                      <input
                        ref={ribFileInputRef}
                        type="file"
                        name="ribDocument"
                        accept=".pdf,image/*"
                        onChange={handleChange}
                        className="w-full outline-none  text-slate-600 file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-100 hover:file:bg-slate-200"
                      />
                    </div>
                  </div>
                </div>
                {ribFileError && (
                  <div className="text-sm mt-2 mb-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 space-y-2">
                    <p>{ribFileError}</p>
                    <button
                      type="button"
                      onClick={handleRemoveRibDocument}
                      className="text-xs font-semibold underline hover:no-underline"
                    >
                      Supprimer ce fichier
                    </button>
                  </div>
                )}
                {!ribFileError && formData.ribDocument && (
                  <p className="text-xs text-emerald-600 mt-2 mb-2">
                    Fichier prêt : {formData.ribDocument.name} (
                    {formatFileSize(formData.ribDocument.size)})
                  </p>
                )}
                {scanning && (
                  <p className="text-sm text-slate-500 mt-2 flex items-center gap-2">
                    <Loader2 className="animate-spin" size={16} />
                    Analyse du document...
                  </p>
                )}
                {ribMessage && (
                  <p className="text-sm mt-2 mb-2 text-slate-600 whitespace-pre-line">
                    {ribMessage}
                  </p>
                )}
                <div className="flex items-center w-full mb-4">
                  <p className="w-1/6 text-slate-400">IBAN : </p>
                  <div className="w-full flex items-center border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-[#8B1538]">
                    <CreditCard className="text-slate-400 mr-2" size={18} />
                    <input
                      type="text"
                      name="rib"
                      value={formData.rib}
                      onChange={handleChange}
                      required
                      className="w-full outline-none "
                      placeholder="Votre RIB"
                    />
                  </div>
                </div>
                <div className="flex items-center w-full">
                  <p className="w-1/6 text-slate-400">BIC : </p>
                  <div className="w-full flex items-center border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-[#8B1538]">
                    <CreditCard className="text-slate-400 mr-2" size={18} />
                    <input
                      type="text"
                      name="bic"
                      value={formData.bic}
                      onChange={handleChange}
                      required
                      className="w-full outline-none "
                      placeholder="Code BIC"
                    />
                  </div>
                </div>
              </div>

              {/* Date Signature */}
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">
                  Signé le
                </label>
                <div className="flex items-center border rounded-xl px-3 py-2 bg-slate-100">
                  <Calendar className="text-slate-400 mr-2" size={18} />
                  <input
                    type="date"
                    name="dateSignature"
                    value={formData.dateSignature}
                    disabled
                    className="w-full outline-none  cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Lieu */}
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">
                  Lieu
                </label>
                <div className="flex items-center border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-[#8B1538]">
                  <PenLine className="text-slate-400 mr-2" size={18} />
                  <input
                    type="text"
                    name="lieu"
                    value={formData.lieu}
                    onChange={handleChange}
                    required
                    className="w-full outline-none "
                    placeholder="Lieu de signature"
                  />
                </div>
              </div>
              {/* <div className="flex items-center">
                <input
                  id="normalSignature"
                  type="checkbox"
                  checked={isNormalSignature}
                  onChange={(e) => setIsNormalSignature(e.target.checked)}
                  className="w-3 h-3 text-[#9B1B5D] border-gray-300 rounded "
                />
                <label
                  htmlFor="normalSignature"
                  className="ml-2 text-slate-400 text-xs font-medium"
                >
                  Envoyer une demande de signature normale
                </label>
              </div> */}

              {/* Bouton */}

              {/* {pdfUrl && ( */}
              {pdfUrl && signingLink && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm text-green-700 mb-2">
                    Mandat généré avec succès :
                  </p>

                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline font-medium block mb-3"
                  >
                    Voir le PDF du mandat
                  </a>
                </div>
              )}
            </form>
            <div className="sticky bottom-0 bg-white border-t px-8 py-4 mt-10">
              <div className="flex flex-col gap-3">
                {/* 🔵 Visualiser */}
                <button
                  type="button"
                  disabled={download || isRibBlocked}
                  onClick={downloadMandat}
                  className="h-11 border-2 border-slate-300 rounded-md flex items-center justify-center gap-2 hover:border-[#7A1548]"
                >
                  {download ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <Download size={18} />
                  )}
                  {download ? "Téléchargement..." : "Visualiser le mandat"}
                </button>

                {/* 🟣 Valider */}
                {!showValidationActions && (
                  <button
                    type="button"
                    disabled={isRibBlocked}
                    onClick={handleValidateClick}
                    className="h-11 bg-[#9B1B5D] text-white rounded-md flex items-center justify-center gap-2 hover:bg-[#7A1548] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FileSignature size={18} />
                    Valider le mandat
                  </button>
                )}

                {/* 🔥 Actions après validation */}
                {showValidationActions && (
                  <div className="flex gap-3">
                    {/* 📥 Create + download */}
                    <button
                      type="button"
                      disabled={isRibBlocked}
                      onClick={createAndDownloadMandat}
                      className="w-1/2 h-11 border rounded-md flex items-center justify-center gap-2 hover:bg-slate-100"
                    >
                      <Download size={18} />
                      Télécharger le Mandat
                    </button>

                    {/* ✍️ Signature */}
                    <button
                      type="button"
                      disabled={signing || isRibBlocked}
                      onClick={handleSubmit}
                      className="w-1/2 h-11 bg-[#9B1B5D] text-white rounded-md flex items-center justify-center gap-2 hover:bg-[#7A1548]"
                    >
                      {signing ? (
                        <Loader2 className="animate-spin" size={18} />
                      ) : (
                        <FileSignature size={18} />
                      )}
                      {signing ? "Envoi..." : "Envoyer au Client"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* PDF VIEWER */}
          <div className="relative w-full lg:w-1/2 mt-20 border lg:border-l">
            <div className="relative w-full lg:aspect-[210/297]">
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer fileUrl={mandatTemplate.pdf} />
              </Worker>
              {/* OVERLAY FIELDS */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Nom */}
                <input
                  type="text"
                  value={formData.nom}
                  onChange={handleChange}
                  name="nom"
                  className="
    absolute 
     
    border 
    bg-red-500/20
    border-[#8B1538]/40
    focus:border-[#8B1538]
    focus:outline-none
    text-[0.5rem] 
    font-bold
    text-slate-800
    pointer-events-auto
    transition-all
    duration-200

  "
                  style={{
                    top: "21%",
                    left: "33%",
                    width: "50%",
                  }}
                />

                {/* Adresse */}
                <input
                  type="text"
                  value={formData.rue}
                  onChange={handleChange}
                  name="rue"
                  className="
    absolute 
     
    border 
    bg-red-500/20
    focus:border-[#8B1538]
    focus:outline-none
    text-[0.5rem] 
    font-bold
    text-slate-800
    pointer-events-auto
    transition-all
    duration-200
  "
                  style={{
                    top: "23.5%",
                    left: "33%",
                    width: "50%",
                  }}
                />

                {/* Complément d'adresse — nouvelle ligne ajoutée dans le
                    visuel du mandat pour les adresses trop longues */}
                <input
                  type="text"
                  value={formData.adresse_complement}
                  onChange={handleChange}
                  name="adresse_complement"
                  className="
    absolute

    border
    bg-red-500/20
    focus:border-[#8B1538]
    focus:outline-none
    text-[0.5rem]
    font-bold
    text-slate-800
    pointer-events-auto
    transition-all
    duration-200
  "
                  style={{
                    top: "25.7%",
                    left: "33%",
                    width: "50%",
                  }}
                />

                <input
                  type="text"
                  value={formData.code_postal}
                  onChange={handleChange}
                  name="code_postal"
                  className="
    absolute

    border
    bg-red-500/20
    focus:border-[#8B1538]
    focus:outline-none
    text-[0.5rem]
    font-bold
    text-slate-800
    pointer-events-auto
    transition-all
    duration-200
  "
                  style={{
                    top: "27.2%",
                    left: "33%",
                    width: "50%",
                  }}
                />

                <input
                  type="text"
                  value={formData.pays}
                  onChange={handleChange}
                  name="pays"
                  className="
    absolute 
     
    border 
    bg-red-500/20
    focus:border-[#8B1538]
    focus:outline-none
    text-[0.5rem] 
    font-bold
    text-slate-800
    pointer-events-auto
    transition-all
    duration-200
  "
                  style={{
                    top: "30%",
                    left: "33%",
                    width: "50%",
                  }}
                />

                <input
                  type="text"
                  value={formData.rib}
                  onChange={handleChange}
                  name="rib"
                  className="
    absolute 
     
    border 
    bg-red-500/20
    focus:border-[#8B1538]
    focus:outline-none
    text-[0.5rem] 
    font-bold
    text-slate-800
    pointer-events-auto
    transition-all
    duration-200
  "
                  style={{
                    top: "33.6%",
                    left: "33%",
                    width: "50%",
                  }}
                />

                <input
                  type="text"
                  value={formData.bic}
                  onChange={handleChange}
                  name="bic"
                  className="
    absolute 
     
    border 
    bg-red-500/20
    focus:border-[#8B1538]
    focus:outline-none
    text-[0.5rem] 
    font-bold
    text-slate-800
    pointer-events-auto
    transition-all
    duration-200
  "
                  style={{
                    top: "35.5%",
                    left: "33%",
                    width: "50%",
                  }}
                />

                <input
                  type="text"
                  value={formatDateFR(formData.dateSignature)}
                  name="dateSignature"
                  className="
    absolute 
     
    border 
    bg-red-500/20
    focus:border-[#8B1538]
    focus:outline-none
    text-[0.5rem] 
    font-bold
    text-slate-800
    pointer-events-auto
    transition-all
    duration-200
  "
                  style={{
                    top: "54.6%",
                    left: "33%",
                    width: "50%",
                  }}
                />

                <input
                  type="text"
                  value={formData.lieu}
                  onChange={handleChange}
                  name="lieu"
                  className="
    absolute 
     
    border 
    bg-red-500/20
    focus:border-[#8B1538]
    focus:outline-none
    text-[0.5rem] 
    font-bold
    text-slate-800
    pointer-events-auto
    transition-all
    duration-200
  "
                  style={{
                    top: "56.5%",
                    left: "33%",
                    width: "50%",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <br />
      </div>
    </div>
  );
}

export default Mandat;
